import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { usePageView } from '@martincastroalvarez/tracking/usePageView';
import { Login } from './Login';
import { LoginEvents } from './events';
import { apexDomain, setApexDomain } from '@martincastroalvarez/runtime';
import { configureTracking } from '@martincastroalvarez/tracking';

// The page's tracker (ra tracking, 2026-10-21): a module value, not a provider.
configureTracking({ ga: import.meta.env.VITE_GOOGLE_TAG_ID });

// The tenant's apex, pinned by the build (`VITE_DOMAIN`), else read off the browser host — set before
// anything renders, so every `apexDomain()` on the page answers the same (ra runtime, 2026-10-20).
setApexDomain(import.meta.env.VITE_DOMAIN);

function App() {
  const domain = apexDomain();
  const siteTitle = import.meta.env.VITE_SITE_TITLE || 'Authentication Portal';
  const siteDescription = import.meta.env.VITE_SITE_DESCRIPTION || 'Personal Authentication Portal';

  // One mount-time page view. The hook is the shared one, so this is a no-op with no tracker
  // configured rather than a branch every call site has to remember.
  usePageView(LoginEvents.PAGE_VIEW);

  // Update document metadata dynamically
  useEffect(() => {
    // Update title
    document.title = `Login - ${domain}`;

    // Update meta tags
    const updateMetaTag = (selector: string, content: string) => {
      const tag = document.querySelector(selector);
      if (tag) {
        tag.setAttribute('content', content);
      }
    };

    // Update description
    updateMetaTag('meta[name="description"]', siteDescription);

    // Update Open Graph
    updateMetaTag('meta[property="og:url"]', `https://login.${domain}/`);
    updateMetaTag('meta[property="og:title"]', siteTitle);
    updateMetaTag('meta[property="og:description"]', siteDescription);
    updateMetaTag(
      'meta[property="og:image"]',
      `https://icons.${domain}/login/apple-touch-icon.png`,
    );

    // Update Twitter
    updateMetaTag('meta[property="twitter:url"]', `https://login.${domain}/`);
    updateMetaTag('meta[property="twitter:title"]', siteTitle);
    updateMetaTag('meta[property="twitter:description"]', siteDescription);
    updateMetaTag(
      'meta[property="twitter:image"]',
      `https://icons.${domain}/login/apple-touch-icon.png`,
    );

    // Update favicon links
    // The favicon, touch icon and manifest are NOT set here: <Anubis> writes the brand's (2026-10-05).
  }, [domain, siteTitle, siteDescription]);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Login />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

const init = async () => {
  // The env vars without which this page cannot do its job. `VITE_GOOGLE_TAG_ID` used to be on
  // this list and is deliberately no longer: a missing tag id now yields a disabled tracker (see
  // `configureTracking` above), not a thrown error. Refusing to mount is the right answer for an
  // OAuth client id, because a sign-in button that cannot sign anyone in is worse than an honest
  // failure — and exactly the wrong answer for analytics, because it made a misconfigured
  // reporting id take down the page the whole fleet signs in through.
  const requiredEnvVars = ['VITE_GOOGLE_OAUTH_CLIENT_ID', 'VITE_LINKEDIN_OAUTH_CLIENT_ID'] as const;
  const missing = requiredEnvVars.filter((v) => !import.meta.env[v]);
  if (missing.length) {
    throw new Error(`Missing required environment variables: ${missing.join(', ')}.`);
  }

  // Mount the app only if the MSW is not enabled
  const root = document.getElementById('root');
  if (!root) throw new Error('Root element not found');
  ReactDOM.createRoot(root).render(
    <React.StrictMode>
      {/* The app shell owns config; the tracking package never reads an env var itself. An empty
          or absent id disables tracking without disabling anything else. */}
      <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID}>
        <App />
      </GoogleOAuthProvider>
    </React.StrictMode>,
  );
};

// Ensure import rejects on initialization errors
init().catch((err: Error) => {
  console.error('Failed to initialize app:', err);
});

export { init };
